import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout';
import PublicLayout from './components/PublicLayout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import MyBadgesPage from './pages/MyBadgesPage';
import AvailableBadgesPage from './pages/AvailableBadgesPage';
import MyAccountPage from './pages/MyAccountPage';
import MyProfilePage from './pages/MyProfilePage';
import IssuersPage from './pages/IssuersPage';
import AdminPage from './pages/AdminPage';
import CreateBadgePage from './pages/CreateBadgePage';
import BadgeDetailPage from './pages/BadgeDetailPage';
import NotFoundPage from './pages/NotFoundPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import VerifyEmailRequiredPage from './pages/VerifyEmailRequiredPage';
import CompleteSignupPage from './pages/CompleteSignupPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BadgeMetricsPage from './pages/BadgeMetricsPage';
import StatisticsReportingPage from './pages/StatisticsReportingPage';
import WatchPage from './pages/WatchPage';
import BadgeEditLoader from './pages/BadgeEditLoader';
import PortfolioPage from './pages/PortfolioPage';
import IssuedBadgesPage from './pages/IssuedBadgesPage';
import NotificationsPage from './pages/NotificationsPage';
import ClaimQRPage from './pages/ClaimQRPage';
import BadgeAccessPage from './pages/BadgeAccessPage';
import HomeDashboardPage from './pages/HomeDashboardPage';
import PublicProfilePage from './pages/PublicProfilePage';
import ExternalAuthPage from './pages/ExternalAuthPage';
import AboutPage from './pages/public/AboutPage';
import HowItWorksPage from './pages/public/HowItWorksPage';
import AudiencePage from './pages/public/AudiencePage';
import WhyFreePage from './pages/public/WhyFreePage';
import FaqsPage from './pages/public/FaqsPage';
import PartnersPage from './pages/public/PartnersPage';
import DevelopersPage from './pages/public/DevelopersPage';
import RoadmapPage from './pages/public/RoadmapPage';
import PrivacyPage from './pages/public/PrivacyPage';
import ContactPage from './pages/public/ContactPage';

function LoadingSpinner() {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
            <CircularProgress />
        </Box>
    );
}

/**
 * Protected route wrapper — redirects to /login if not authenticated, and to
 * /verify-email-required if the user hasn't confirmed their email yet.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return <LoadingSpinner />;
    if (!user) {
        return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
    }
    if (!user.emailVerified && location.pathname !== '/verify-email-required') {
        return <Navigate to="/verify-email-required" replace />;
    }
    return <>{children}</>;
}

/**
 * Admin-only route wrapper — redirects to / if not an admin.
 */
function ProtectedAdminRoute({ children }: { children: React.ReactNode }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return <LoadingSpinner />;
    if (!user) {
        return <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />;
    }
    if (!user.isAdmin) return <Navigate to="/" replace />;
    return <>{children}</>;
}

/**
 * Home is Discover (Earn a Badge) for everyone — admins and non-admins alike.
 * Admins still reach the admin console directly at /dashboard.
 */
function RoleAwareHome() {
    const { loading } = useAuth();
    if (loading) return <LoadingSpinner />;
    return <Navigate to="/available-badges" replace />;
}

export default function App() {
    return (
        <BrowserRouter>
            <Routes>
                {/* Public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route
                    path="/complete-signup"
                    element={
                        <ProtectedRoute>
                            <CompleteSignupPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/verify-email-required"
                    element={
                        <ProtectedRoute>
                            <VerifyEmailRequiredPage />
                        </ProtectedRoute>
                    }
                />
                <Route path="/badges/claim/:code" element={<ClaimQRPage />} />
                <Route path="/external-auth" element={<ExternalAuthPage />} />

                {/* Protected routes under shared layout */}
                <Route
                    element={
                        <ProtectedRoute>
                            <Layout />
                        </ProtectedRoute>
                    }
                >
                    <Route path="/" element={<RoleAwareHome />} />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedAdminRoute>
                                <AdminPage />
                            </ProtectedAdminRoute>
                        }
                    />
                    <Route
                        path="/reports"
                        element={
                            <ProtectedAdminRoute>
                                <StatisticsReportingPage />
                            </ProtectedAdminRoute>
                        }
                    />
                    <Route path="/home" element={<HomeDashboardPage />} />
                    <Route path="/badges" element={<MyBadgesPage />} />
                    <Route path="/badges/create" element={<CreateBadgePage />} />
                    <Route path="/badges/:id/edit" element={<BadgeEditLoader />} />
                    <Route path="/badges/:id/metrics" element={<BadgeMetricsPage />} />
                    <Route path="/badges/:id/access" element={<BadgeAccessPage />} />
                    <Route path="/watch/:id" element={<WatchPage />} />
                    <Route path="/badges/:id" element={<BadgeDetailPage />} />
                    <Route path="/available-badges" element={<AvailableBadgesPage />} />
                    <Route path="/portfolio" element={<PortfolioPage />} />
                    <Route path="/issued" element={<IssuedBadgesPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/endorsements" element={<Navigate to="/notifications" replace />} />
                    <Route path="/account" element={<MyAccountPage />} />
                    <Route path="/profile" element={<MyProfilePage />} />
                    <Route path="/issuers" element={<IssuersPage />} />
                </Route>

                {/* Public marketing pages */}
                <Route element={<PublicLayout />}>
                    <Route path="/u/:slug" element={<PublicProfilePage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/how-it-works" element={<HowItWorksPage />} />
                    <Route path="/audience" element={<AudiencePage />} />
                    <Route path="/why-free" element={<WhyFreePage />} />
                    <Route path="/faqs" element={<FaqsPage />} />
                    <Route path="/partners" element={<PartnersPage />} />
                    <Route path="/developers" element={<DevelopersPage />} />
                    <Route path="/roadmap" element={<RoadmapPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                </Route>

                {/* Fallback */}
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
        </BrowserRouter>
    );
}
